// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: small pieces both the desktop and phone workspace use — the status
// ring and pill (ClickUp's status colour, filling up as a task moves through
// its list), the space tile and the priority flag.

import {FALLBACK_COLOR, StatusRing} from '@app/features/konvo_tasks/components/workspace/shared/StatusRing';
import styles from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits.module.css';
import type {KonvoTask, KonvoTaskListStatus, KonvoTaskStatus} from '@app/features/konvo_tasks/state/KonvoTasks';
import {statusLabel} from '@app/features/konvo_tasks/state/KonvoTasks';
import {FlagIcon} from '@app/features/ui/icons/Icons';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {clsx} from 'clsx';
import type React from 'react';

export {
	isClosedStatus,
	StatusRing,
	statusProgress,
} from '@app/features/konvo_tasks/components/workspace/shared/StatusRing';

export function StatusPill({
	status,
	statuses,
	children,
	className,
}: {
	status: KonvoTaskStatus;
	statuses?: Array<KonvoTaskListStatus>;
	children?: React.ReactNode;
	className?: string;
}) {
	return (
		<span
			className={clsx(styles.pill, className)}
			style={{'--status-color': status.color ?? FALLBACK_COLOR} as React.CSSProperties}
		>
			<StatusRing status={status} statuses={statuses} />
			{children ?? statusLabel(status.status)}
		</span>
	);
}

export function SpaceTile({
	name,
	color,
	size = 22,
	className,
}: {
	name: string;
	color: string | null;
	size?: number;
	className?: string;
}) {
	const initial = Array.from(name.trim())[0] ?? '?';
	return (
		<span
			className={clsx(styles.space, className)}
			style={{'--space-color': color ?? FALLBACK_COLOR, '--tile-size': `${size}px`} as React.CSSProperties}
			aria-hidden
		>
			{initial}
		</span>
	);
}

export const PRIORITY_DESCRIPTORS: Record<number, MessageDescriptor> = {
	1: msg({message: 'Urgent', comment: 'ClickUp priority 1.'}),
	2: msg({message: 'High', comment: 'ClickUp priority 2.'}),
	3: msg({message: 'Normal', comment: 'ClickUp priority 3.'}),
	4: msg({message: 'Low', comment: 'ClickUp priority 4.'}),
};

const FLAG_CLASS: Record<number, string> = {
	1: styles.flagUrgent,
	2: styles.flagHigh,
	3: styles.flagNormal,
	4: styles.flagLow,
};

/** ClickUp priority id (1 urgent … 4 low), or null. */
export function priorityOf(task: Pick<KonvoTask, 'priority'>): number | null {
	const id = task.priority?.id;
	return id != null && id >= 1 && id <= 4 ? id : null;
}

export function PriorityFlag({priority, className}: {priority: number | null; className?: string}) {
	return (
		<FlagIcon
			className={clsx(styles.flag, priority ? FLAG_CLASS[priority] : styles.flagNone, className)}
			weight={priority ? 'fill' : 'regular'}
		/>
	);
}
